// Marketplace payments client. Sibling of ./client.ts (bare reads).
// Reads (`getPaymentsProfile`, `getCreditLedger`) carry the wallet-api
// delegation bearer — the same credential the vault data plane already reads
// vault-api with (see lib/clients/vault/read-client.ts) — because a boot-time
// mount effect has no user gesture to open a passkey step-up popup with, and
// `resume()` never restores signing keys. Writes (`cancelSubscription`) still
// sign: they run inside a user's click, where a step-up is allowed.
// Wire contract verified against marketplace lib/payments/route-auth.ts +
// lib/clients/marketplace/payments.ts (spec §4); the marketplace accepts
// EITHER credential on the read routes, so this is not a flag day.

import { z } from 'zod';
import type { Signer } from '@cef-ai/signer';
import { canonicalizeBytes } from '@/lib/jcs';
import { bytesToHex } from '@/lib/encoding/hex';
import { marketplaceFetchUrl } from './client';

export interface PaymentsCard { brand: string | null; last4: string | null; expMonth: number | null; expYear: number | null }
export interface PaymentsSubscription {
  subscriptionId: string; agentId: string; tierId: string | null; amountUsd: string;
  renewsAt: string | null; status: string; period: string | null;
  creditsGrantedNc: number; creditsAvailableNc: number;
  // Carries the *newest* grant's expiry, not the soonest — never date
  // subscription copy from this; use renewsAt instead.
  creditsExpiresAt: string | null;
}
export interface PaymentsAgentCredit { agentId: string; creditsAvailableNc: number; creditsExpiresAt: string | null; sources: string[] }
export interface PaymentsProfile {
  accountPubkey: string; registered: boolean; hasActiveCard: boolean;
  card: PaymentsCard | null; subscriptions: PaymentsSubscription[]; agentCredits: PaymentsAgentCredit[];
}
// The one response in this client that is checked at runtime, not just
// asserted. `request<T>` casts, so a field rename upstream degrades to
// `undefined` and formats as NaN — which is how the ledger shipped rendering
// "+NaN". Ledger-only on purpose: these numbers are the ones the UI does
// arithmetic on, and the types below are inferred from the schema so the
// declaration cannot drift from what is verified. Not a validation layer for
// the whole client.
// .safe() and not a bare number: 2^53 nanocredits is ~9.0M credits, so a
// large balance arrives from payment-service's i64 already rounded by
// JSON.parse. Nothing downstream can detect that, so it is rejected here.
const LedgerEntrySchema = z.object({
  id: z.string(), entryType: z.string(), agentId: z.string().nullable(),
  amountCreditsNc: z.number().safe(), amountUsd: z.string().nullable(), runningBalanceNc: z.number().safe(),
  expiresAt: z.string().nullable(), createdAt: z.string(),
});
const LedgerPageSchema = z.object({
  entries: z.array(LedgerEntrySchema), total: z.number(), limit: z.number(), offset: z.number(),
});
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;
export type LedgerPage = z.infer<typeof LedgerPageSchema>;
export interface CancelResult { subscriptionId: string; status: string; creditsAvailableNc: number; burnsAt: string | null }

const TIMEOUT_MS = 5_000;

export class PaymentsError extends Error {
  constructor(message: string, readonly status: number, readonly code: string | null) {
    super(message);
    this.name = 'PaymentsError';
  }
}

// Bare lowercase hex, no `0x` — the proxy derives account_pubkey from this
// header verbatim. Deliberately NOT CereWallet.publicKeyHex, which carries a
// `0x` prefix and would fail verification server-side.
function barePubkey(signer: Signer): string {
  return bytesToHex(signer.publicKey);
}

function bearerHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

async function signedBody(
  signer: Signer,
  body: Record<string, unknown>,
): Promise<{ headers: Record<string, string>; payload: string }> {
  const canonical = canonicalizeBytes({ ...body, issuedAt: new Date().toISOString(), nonce: crypto.randomUUID() });
  // Intent pinned to "data" — matches lib/clients/ddc/gateway-auth.ts. Today
  // this is a no-op: CefSigner.sign() defaults to the same intent, and the
  // signed bytes are identical either way. Pinning it stops that from being
  // an assumption — a future default change or a signer implementation that
  // requires an explicit intent would otherwise fail signature verification
  // with no type error to catch it.
  const sig = await signer.sign(canonical, 'data');
  return {
    headers: {
      'content-type': 'application/json',
      'X-Wallet-Pubkey': barePubkey(signer),
      'X-Wallet-Signature': bytesToHex(sig),
    },
    payload: new TextDecoder().decode(canonical),
  };
}

async function request<T>(url: string, init: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { ...init, signal: AbortSignal.timeout(TIMEOUT_MS) });
  } catch (err) {
    throw new PaymentsError(
      `payments request failed (network): ${err instanceof Error ? err.message : String(err)}`,
      0,
      null,
    );
  }
  if (!res.ok) {
    let code: string | null = null;
    let message = `HTTP ${res.status}`;
    try {
      const parsed = (await res.json()) as { error?: string; message?: string };
      code = parsed.error ?? null;
      message = parsed.message ?? message;
    } catch { /* non-JSON error body */ }
    throw new PaymentsError(message, res.status, code);
  }
  return (await res.json()) as T;
}

export async function getPaymentsProfile(baseUrl: string, token: string): Promise<PaymentsProfile> {
  const url = marketplaceFetchUrl(baseUrl, '/api/v1/payments/me');
  return request<PaymentsProfile>(url, { headers: bearerHeaders(token) });
}

export async function getCreditLedger(
  baseUrl: string,
  token: string,
  limit = 20,
  offset = 0,
): Promise<LedgerPage> {
  const url = marketplaceFetchUrl(baseUrl, `/api/v1/payments/me/credit-ledger?limit=${limit}&offset=${offset}`);
  const parsed = LedgerPageSchema.safeParse(await request<unknown>(url, { headers: bearerHeaders(token) }));
  if (!parsed.success) {
    // status 0: the HTTP call succeeded, so there is no meaningful status —
    // same convention as the network-failure case above.
    throw new PaymentsError(`credit-ledger wire contract mismatch: ${parsed.error.message}`, 0, 'WIRE_CONTRACT');
  }
  return parsed.data;
}

export async function cancelSubscription(
  baseUrl: string,
  signer: Signer,
  agentId: string,
): Promise<CancelResult> {
  const url = marketplaceFetchUrl(baseUrl, `/api/v1/payments/subscriptions/${encodeURIComponent(agentId)}`);
  const { headers, payload } = await signedBody(signer, { action: 'cancel-subscription', agentId });
  return request<CancelResult>(url, { method: 'DELETE', headers, body: payload });
}
